import { supabase } from '../supabaseClient';
import type { AnnualTimetableCell } from '../types/domain';
import type { AnnualProjectProgram } from '../utils/annualProjectProgramsStorage';
import { invalidateSchoolScopedDataCaches } from '../utils/schoolDataCacheVersion';

type TeamLeaderAnnualBundleRaw = {
  leader_workspace_id?: unknown;
  source_grade_id?: unknown;
  source_academic_year?: unknown;
  cells?: unknown;
  projects?: unknown;
};

export type TeamLeaderAnnualBundlePayload = {
  leaderWorkspaceId: string;
  sourceGradeId: string;
  sourceAcademicYear: number | null;
  cells: AnnualTimetableCell[];
  projects: AnnualProjectProgram[];
};

function parseCells(raw: unknown): AnnualTimetableCell[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const o = row as Record<string, unknown>;
      return {
        id: String(o.id ?? ''),
        workspace_id: String(o.workspace_id ?? ''),
        grade_id: String(o.grade_id ?? ''),
        academic_year: Number(o.academic_year ?? 0) || 0,
        term_number: Number(o.term_number) === 2 ? 2 : 1,
        monday_iso: String(o.monday_iso ?? ''),
        weekday_key: (String(o.weekday_key ?? 'mon') as AnnualTimetableCell['weekday_key']),
        slot_index: Number(o.slot_index ?? 0) || 0,
        text_value: String(o.text_value ?? ''),
        memo: o.memo == null ? null : String(o.memo),
        color_hex: o.color_hex == null ? null : String(o.color_hex),
        slot_kind: (String(o.slot_kind ?? 'class') as AnnualTimetableCell['slot_kind']),
        is_editable: Boolean(o.is_editable),
        is_dedicated: Boolean(o.is_dedicated),
        is_base_excluded: Boolean(o.is_base_excluded),
      } as AnnualTimetableCell;
    })
    .filter((row): row is AnnualTimetableCell => row != null && !!row.grade_id);
}

function parseProjects(raw: unknown): AnnualProjectProgram[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const o = row as Record<string, unknown>;
      const term = Number(o.term_number) === 2 ? 2 : 1;
      return {
        id: String(o.id ?? ''),
        name: String(o.name ?? ''),
        colorHex: String(o.color_hex ?? ''),
        termNumber: term,
      } as AnnualProjectProgram;
    })
    .filter((row): row is AnnualProjectProgram => row != null && !!row.name.trim());
}

export async function fetchTeamLeaderAnnualTimetableBundle(
  teamId: string,
  targetWorkspaceId: string,
  sourceGradeId: string,
  preferredAcademicYear: number
): Promise<TeamLeaderAnnualBundlePayload> {
  const { data, error } = await supabase.rpc('import_team_annual_timetable_bundle', {
    p_team_id: teamId,
    p_target_workspace_id: targetWorkspaceId,
    p_source_grade_id: sourceGradeId,
    p_preferred_academic_year: preferredAcademicYear,
  });
  if (error) throw new Error(error.message);
  const raw = (data ?? {}) as TeamLeaderAnnualBundleRaw;
  const sourceAcademicYear = Number(raw.source_academic_year);
  return {
    leaderWorkspaceId: String(raw.leader_workspace_id ?? ''),
    sourceGradeId: String(raw.source_grade_id ?? sourceGradeId),
    sourceAcademicYear: Number.isFinite(sourceAcademicYear) && sourceAcademicYear > 0 ? sourceAcademicYear : null,
    cells: parseCells(raw.cells),
    projects: parseProjects(raw.projects),
  };
}

/** @deprecated 읽기 전용 RPC + 캐시 invalidate. 신규 코드는 fetch + 필요 시 invalidate 분리 사용 */
export async function importTeamLeaderAnnualTimetableBundle(
  teamId: string,
  targetWorkspaceId: string,
  sourceGradeId: string,
  preferredAcademicYear: number
): Promise<TeamLeaderAnnualBundlePayload> {
  const payload = await fetchTeamLeaderAnnualTimetableBundle(
    teamId,
    targetWorkspaceId,
    sourceGradeId,
    preferredAcademicYear
  );
  await invalidateSchoolScopedDataCaches(targetWorkspaceId);
  return payload;
}
